import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

// The URL reader lives on the interview simulator, which already runs on 3030 in
// dev and simulation.mploi.com in prod (same pattern the interview launch uses).
const SIM_BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:3030/'
  : 'https://simulation.mploi.com/';

const InterviewJobs: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // "Paste a job link" state
  const [jobUrl, setJobUrl] = useState('');
  const [reading, setReading] = useState(false);
  const [readMsg, setReadMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    try {
      const r = await api.get(END_POINTS.interviewJobs, {});
      setJobs(Array.isArray(r) ? r : (r?.results || []));
    } catch (e: any) { ValidationErrorToast('Error', e?.message || 'Load failed'); }
    finally { setPageLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const readJobUrl = async () => {
    const url = jobUrl.trim();
    if (!url) return setReadMsg({ ok: false, text: 'Paste a job link first, or just type the description below.' });
    if (!/^https?:\/\//i.test(url)) {
      return setReadMsg({ ok: false, text: "That doesn't look like a web link — it should start with http:// or https://" });
    }
    setReading(true);
    setReadMsg(null);
    try {
      const resp = await fetch(`${SIM_BASE}read-job-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await resp.json();
      if (data && data.ok) {
        if (data.title) setTitle(data.title);
        setDesc(data.description || '');
        setReadMsg({ ok: true, text: 'Got it — filled in below. Have a read and edit anything before you save.' });
      } else {
        setReadMsg({ ok: false, text: (data && data.message) || "We couldn't read that link. Paste the description below instead." });
      }
    } catch (e) {
      setReadMsg({ ok: false, text: "Couldn't reach the reader. Paste the description below instead." });
    } finally {
      setReading(false);
    }
  };

  const create = async () => {
    if (!title.trim()) return ValidationErrorToast('Required', 'Title required');
    setLoading(true);
    try {
      await api.post(END_POINTS.interviewJobs, { title, description: desc });
      SuccessToast('Created', 'Job created');
      setTitle(''); setDesc('');
      load();
    } catch (e: any) { ValidationErrorToast('Error', e?.message || 'Create failed'); }
    finally { setLoading(false); }
  };

  if (pageLoading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading jobs...</p>
        </div>
      </InterviewSidebar>
    );
  }

  return (
    <InterviewSidebar>
      {/* Create Job Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <h3 style={styles.cardTitle}>Create New Job</h3>

        {/* Paste a job link → we read the page and fill the fields */}
        <div style={{ background: '#F4F8FF', border: '1px solid #D6E4FF', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <label style={{ fontWeight: 600, fontSize: '14px', color: '#141414', display: 'block', marginBottom: '4px' }}>
            Paste a job link
          </label>
          <p style={{ ...styles.secondaryText, marginTop: 0, marginBottom: '12px' }}>
            From Seek, Indeed, a company careers page — we'll read it and fill in the title and description below. You can edit anything before saving.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              style={{ ...styles.modalInput, flex: 1, minWidth: '260px', marginTop: 0 }}
              placeholder="https://www.seek.com.au/job/..."
              value={jobUrl}
              onChange={e => setJobUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') readJobUrl(); }}
            />
            <button
              style={{ ...styles.button, ...styles.primaryButton, whiteSpace: 'nowrap' } as any}
              onClick={readJobUrl}
              disabled={reading}
            >
              {reading ? 'Reading…' : 'Read this job'}
            </button>
          </div>
          {readMsg && (
            <p style={{ marginTop: '10px', marginBottom: 0, fontSize: '13px', color: readMsg.ok ? '#1a8f4c' : '#c53030' }}>
              {readMsg.text}
            </p>
          )}
          <p style={{ ...styles.secondaryText, marginTop: '10px', marginBottom: 0, fontSize: '12px' }}>
            No link? Just type or paste the description below.
          </p>
        </div>

        <input
          style={styles.modalInput}
          placeholder="Job title"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <textarea
          style={{ ...styles.modalInput, minHeight: '100px', marginTop: '12px' } as any}
          placeholder="Description / requirements"
          value={desc}
          onChange={e => setDesc(e.target.value)}
        />
        <div style={{ marginTop: '16px' }}>
          <button
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={create}
            disabled={loading}
          >
            {loading ? 'Creating…' : 'Create Job'}
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Jobs ({jobs.length})</h3>
        {jobs.length === 0 ? (
          <div style={{ ...styles.emptyState, minHeight: '200px' }}>
            <p style={styles.secondaryText}>No jobs created yet. Create your first job above.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeader, width: '40%' }}>Title</th>
                  <th style={{ ...styles.tableHeader, width: '40%' }}>Description</th>
                  <th style={{ ...styles.tableHeader, width: '10%', textAlign: 'center' }}>Status</th>
                  <th style={{ ...styles.tableHeader, width: '10%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id}>
                    <td style={styles.tableCell}>
                      <strong style={styles.primaryText}>{j.title}</strong>
                    </td>
                    <td style={styles.tableCell}>
                      {j.description?.slice(0, 80)}{j.description?.length > 80 ? '…' : ''}
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: j.isActive !== false ? '#28a745' : '#6c757d',
                        color: '#ffffff',
                      }}>
                        {j.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Link
                          to={`/interview-simulator/jobs/${j.id}`}
                          style={{ ...styles.button, ...styles.secondaryButton, textDecoration: 'none', padding: '6px 12px', fontSize: '13px' } as any}
                        >
                          Manage
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </InterviewSidebar>
  );
};

export default InterviewJobs;
